import React from "react";
import { ScreenShell, SelectableCard } from "../components";
import type { GoalDirection } from "../types";

type Props = {
  step: number;
  total: number;
  value: GoalDirection | null;
  onChange: (v: GoalDirection) => void;
  onNext: () => void;
  onBack: (() => void) | null;
  isMobile: boolean;
};

export const GOAL_ICONS = {
  // Stylized chart-trend marks matching the design system's desktop goal step.
  lose: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 7l6 6 4-4 8 8" />
      <path d="M14 17h7v-7" />
    </svg>
  ),
  maintain: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <path d="M7 12h10" />
    </svg>
  ),
  gain: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 17l6-6 4 4 8-8" />
      <path d="M14 7h7v7" />
    </svg>
  ),
} as const;

const ICONS = GOAL_ICONS;

export function GoalScreen({ step, total, value, onChange, onNext, onBack, isMobile }: Props): React.ReactElement {
  return (
    <ScreenShell
      step={step}
      total={total}
      title="What's your goal?"
      subtitle="Pick a direction. You can change it later from settings."
      onBack={onBack}
      isMobile={isMobile}
      primaryLabel="Continue"
      primaryDisabled={value === null}
      onPrimary={onNext}
    >
      <div style={{ display: "grid", gap: 12 }}>
        <SelectableCard
          selected={value === "lose"}
          onClick={() => onChange("lose")}
          icon={ICONS.lose}
          title="Lose weight"
          body="Cut phase. You'll get a daily calorie target below your TDEE."
        />
        <SelectableCard
          selected={value === "maintain"}
          onClick={() => onChange("maintain")}
          icon={ICONS.maintain}
          title="Maintain"
          body="Hold your current weight. Daily target matches your TDEE."
        />
        <SelectableCard
          selected={value === "gain"}
          onClick={() => onChange("gain")}
          icon={ICONS.gain}
          title="Build muscle"
          body="Lean bulk. Your target sits above TDEE in a controlled surplus."
        />
      </div>
    </ScreenShell>
  );
}
